import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { IStore } from '../../interfaces';
import { IScarfInfo, EnumSection, OrderCategoriesEnum, IOrderBy, IPagingInfo, ITrophyTooltip, IDetailTrophyTooltip } from '../../interfaces/clientInterfaces';
import * as actions from '../../actions';
import { ALL_COUNTRIES_NAME, ITEMS_PER_PAGE_ITEMS, SHOW_MAP_KEY, SELECT_BY_COUNTRIES, SELECTED_COUNTRIES, HONOURS_ORDER_KEY, NUMBER_HONOURS_ORDER_KEY, YEAR_ORDER_KEY, CATEGORY_ORDER_KEY, initialState, SORT_TITLE_KEY, STADIUM_ORDER_KEY } from '../constants';
import TeamInfoRenderer from './TeamInfoRenderer';
import { StyledAnimatedOnHover } from './SideTree';
import TrophyTooltip from './TrophyTooltip';
import { Link } from 'react-router-dom';
import { Localizer } from '../../utils/localizer';
import DetailTrophyTooltip from './DetailTrophyTooltip';
import { Countries } from '../../utils/countries';
import ReserveTeamTooltip from './ReserveTeamTooltip';
import { useAppDispatch } from '../../utils/modules';
import { ISelectItem, Select } from './Select/Select';
import styles from './TeamGalleryContent.module.css';

//#region styled Components
const ContentContainer = styled.div`
	display: flex;
	flex-direction: column;
	overflow-y: auto;
`;

const TitleContainerWithoutPadding = styled.div`
	padding: 5px 0 5px 0;
	margin: 0;
	text-shadow: -1px 0 rgb(248,149,4), 0 1px rgb(248,149,4), 1px 0 rgb(248,149,4), 0 -1px rgb(248,149,4);

`;
const OrderContainer = styled.div`
	padding: 10px 27px 10px 10px;
	display: flex;
	margin: 0 auto 0 auto;
	flex-direction: column;
`;

const SortContainer = styled.div`
	display: flex;
	margin-right: 10px
`;
const PageNumberInput = styled.input`
	color: white;
	font-size: 16px;
	line-height: 16px;
	width: 20px;
	text-align: center;
	background-color: transparent;
	border: none;
	padding: 5px 0 5px 0;
	text-shadow: -1px 0 rgb(248,149,4), 0 1px rgb(248,149,4), 1px 0 rgb(248,149,4), 0 -1px rgb(248,149,4);
	:focus {
		outline: none;
	}
`;

const DropdownContainerPaging = styled.div`
	float: right;
	margin: auto 10px auto 10px;
`;

const GalleryContainer = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	-webkit-background-size: cover;
	-moz-background-size: cover;
	-o-background-size: cover;
	background-size: cover;
	width: 100vw;
	background-repeat: no-repeat;
	background-position: center center;
	transition: background-image 0.5s;
`;

const IconPagingContainer = styled(StyledAnimatedOnHover)`
	cursor: pointer;
	margin: auto;
	padding: 0 3px 0 3px;
	font-size: 12px;
	line-height: 12px;
	text-shadow: -1px 0 rgb(248,149,4), 0 1px rgb(248,149,4), 1px 0 rgb(248,149,4), 0 -1px rgb(248,149,4);
	:hover {
		color: #F89504;
	}
`;

const MapIconContainer = styled(StyledAnimatedOnHover)`
	display: flex;
	margin: 0 5px 0 5px;
	:hover {
		color: #F89504;
		i {
			color: #F89504;
		}
	}
	a {
		color: transparent;
	}
	i {
		font-size: 15px;
		line-height: 30px;
		cursor: pointer;
		color: white;
		text-shadow: -1px 0 rgb(248,149,4), 0 1px rgb(248,149,4), 1px 0 rgb(248,149,4), 0 -1px rgb(248,149,4);
	}
`;

const ImageLinkContainer = styled.img`
	max-width: 23px;
	margin-right: 5px;
`;

const SelectCountryFlexContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	-webkit-background-size: cover;
	-moz-background-size: cover;
	-o-background-size: cover;
	background-size: cover;
	width: 100vw;
	background-repeat: no-repeat;
	background-position: center center;
	transition: background-image 0.5s;
`;

interface IStyledCountryRow {
	isSelected: boolean;
}
const StyledCountryRow = styled(StyledAnimatedOnHover)<IStyledCountryRow>`
	font-family: Europa-Big;
	font-size: 16px;
	text-align: center;
	margin: auto;
	cursor: pointer;
	text-align: left;
	padding: 1.5px;
	${props => props.isSelected
		? 'background-color: #F89504;'
		: ''}
	a {
		text-decoration: none;
		color: white;
	}
	:hover {
		background-color: #F89504;
		a {
			color: black;
		}
	}
`;

const StyledFilterByCountryButton = styled(StyledAnimatedOnHover)`
	margin-bottom: 25px;
	font-family: Europa-Big;
	font-size: 16px;
	text-align: center;
	margin: 10px auto;
	cursor: pointer;
	padding: 10px;
	background-color: #F89504;
	a {
		text-decoration: none;
		color: black;
	}
	:hover {
		background-color: #F89504;
		a {
			color: black;
		}
	}
`;

const CountryListContainer = styled.div`
	margin: auto;
	padding: 20px;
	overflow-y: auto;
	position: relative;
	height: 100%;
	background-color:rgba(0, 0, 0, 0.3);
`;

const FilterContainerRow = styled.div`
	flex-direction: row;
	margin: auto;
	gap: 10px;
`;

const FilterContainerTopRow = styled.div`
	display: flex;
	flex-direction: row;
	margin: auto auto 5px auto;
`;

const FilterContainerIconsRow = styled.div`
	display: flex;
	flex-direction: row;
`;

//#endregion

const orderByCategoryItems: ISelectItem[] = [
	{
		id: OrderCategoriesEnum.honours,
		text: `${SORT_TITLE_KEY} ${HONOURS_ORDER_KEY}`
	},
	{
		id: OrderCategoriesEnum.numberTitles,
		text: `${SORT_TITLE_KEY} ${NUMBER_HONOURS_ORDER_KEY}`
	},
	{
		id: OrderCategoriesEnum.foundation,
		text: `${SORT_TITLE_KEY} ${YEAR_ORDER_KEY}`
	},
	{
		id: OrderCategoriesEnum.category,
		text:  `${SORT_TITLE_KEY} ${CATEGORY_ORDER_KEY}`
	},
	{
		id: OrderCategoriesEnum.stadium,
		text:  `${SORT_TITLE_KEY} ${STADIUM_ORDER_KEY}`
	},
];

const SCARVES_VIEW = 'Show only scarves';
const EXTENDED_VIEW = 'Show scarves and team info';

const views: ISelectItem[] = [
	{
		id: SCARVES_VIEW,
		text: SCARVES_VIEW,
	},
	{
		id: EXTENDED_VIEW,
		text: EXTENDED_VIEW,
	}
];

interface IGalleryOwnProps {
	code?: string;
	name?: string;
}
export default function Gallery({
	code,
	name
}: IGalleryOwnProps) {

	const dispatch = useAppDispatch();

	const bckContainer = useRef<HTMLDivElement>(null);
	const scrollContainer = useRef<HTMLDivElement>(null);
	
	useEffect(() => {
		dispatch(actions.setSectionName(EnumSection.gallery));
	}, [dispatch]);

	useEffect(() => {
		dispatch(actions.setInfoOfCountry(code));
	}, [code, dispatch]);

	const bckImageOnLoad = useCallback(
		(bckImage: string): any => {
			if (bckContainer.current) {
				bckContainer.current.style.backgroundImage = `url('../img/background/${bckImage}.jpg')`;
			}
		},
		[]
	);

	const countryCodes = useSelector(
		(completeState: IStore): string[] => { 
			return completeState?.general?.countryCodes ?? [];
		});
	
	useEffect(
		() => {
			let bckImageCode = '1';
			if (code) {
				if (
					code === ALL_COUNTRIES_NAME ||
					code === SELECT_BY_COUNTRIES ||
					code === SELECTED_COUNTRIES
				) {
					const countryCodeIndex = Math.floor(Math.random() * (countryCodes.length - 1 - 0 + 1) + 0);
					if (countryCodeIndex < countryCodes.length) {
						bckImageCode = countryCodes[countryCodeIndex];
					} else {
						bckImageCode = countryCodes[countryCodes.length -1];
					}
				} else if (Countries.isCountryCodeConfederation(code)) {
					do {
						const countryCodeIndex = Math.floor(Math.random() * (countryCodes.length - 1 - 0 + 1) + 0);
						if (countryCodeIndex < countryCodes.length) {
							bckImageCode = countryCodes[countryCodeIndex];
						} else {
							bckImageCode = countryCodes[countryCodes.length -1];
						}
					} while (Countries.getConfederationOfCountry(bckImageCode) !== code)
				}			
				else {
					bckImageCode = `${code}`;
				}
			}
			var image = new Image();
			image.src = `./../../img/background/${bckImageCode}.jpg`;
			image.onload = bckImageOnLoad(bckImageCode);
		},
		[bckImageOnLoad, code, countryCodes]
	);
	
	const [renderSelectByCountry, setRenderSelectByCountry] = useState(code === SELECT_BY_COUNTRIES);

	const isReducedView = useSelector(
		(completeState: IStore): boolean => { 
			return completeState?.ui?.isReducedView ?? false;
		});

	const currentGroupScarves = useSelector(
		(completeState: IStore): IScarfInfo[] => {
			if (!completeState || !completeState.scarves) {
				return [];
			}
			let currentGroupScarves = completeState.scarves.currentGroupScarves;
			const start = completeState.orderAndPaging.allPagingInfo.currentPage * completeState.orderAndPaging.allPagingInfo.teamsPerPage;
			const end = start + completeState.orderAndPaging.allPagingInfo.teamsPerPage;
			currentGroupScarves = currentGroupScarves.slice(
				start,
				end >= currentGroupScarves.length ? currentGroupScarves.length : end
			);
			return currentGroupScarves;
		});


	const orderBy = useSelector(
		(completeState: IStore): IOrderBy => { 
			return completeState?.orderAndPaging?.orderBy ?? initialState.orderAndPaging.orderBy;
		});


	const allPagingInfo = useSelector(
		(completeState: IStore): IPagingInfo => { 
			return completeState?.orderAndPaging?.allPagingInfo ?? initialState.orderAndPaging.allPagingInfo;
		});

	const section = useSelector(
		(completeState: IStore): EnumSection => { 
			return completeState?.ui?.section ?? EnumSection.home;
		});

	const trophyTooltip = useSelector(
		(completeState: IStore): ITrophyTooltip | null => { 
			return completeState?.ui?.trophyTooltip ?? null;
		});

	const detailTrophyTrophy = useSelector(
		(completeState: IStore): IDetailTrophyTooltip | null => { 
			return completeState?.ui?.detailTrophyTooltip ?? null;
		});

	const currentCountry = useSelector(
		(completeState: IStore): string => { 
			return completeState?.ui?.currentCountry ?? '';
		});

	const { regions, subRegions } = useSelector(
		(completeState: IStore): {
			regions: ISelectItem[];
			subRegions: ISelectItem[];
		} => {
			if (!completeState || !completeState.ui || !completeState.teams) {
				return {
					regions: [],
					subRegions: []
				};
			}
			const regionsSet = new Set<string>();
			const subRegionsSet = new Set<string>();
			if (Countries.isCountrySelectableByRegion(completeState.ui.currentCountry)) {
				const teams = completeState.teams.teamsByCountryCode[completeState.ui.currentCountry];
				if (teams) {
					for (const team of teams) {
						regionsSet.add(team.city?.region);
						if ((!orderBy.orderByRegion || orderBy.orderByRegion === team.city?.region) && team.city?.subRegion) {
							subRegionsSet.add(team.city?.subRegion);
						}
					}
				}
			}
			const regionsAlphabetically = Array.from(regionsSet).map(reg => ({ text: reg, id: reg, icon: [`./../../img/flag/${reg}.png`] }) ).sort((a,b) => a.text > b.text ? 1 : -1);
			const regions = [
				{
					text: 'All',
					id: completeState.ui.currentCountry,
					icon: [`./../../img/flag/${completeState.ui.currentCountry}.png`]
				},
				...regionsAlphabetically
			];
			const subRegionsAlphabetically = Array.from(subRegionsSet).map(reg => ({ text: reg, id: reg, icon: [`./../../img/flag/${reg}.png`] }) ).sort((a,b) => a.text > b.text ? 1 : -1);
			const subRegions = [
				{
					text: 'All',
					id: completeState.ui.currentCountry,
					icon: [`./../../img/flag/${orderBy.orderByRegion ? orderBy.orderByRegion : completeState.ui.currentCountry}.png`]
				},
				...subRegionsAlphabetically
			];
			return {
				regions,
				subRegions
			};
		});

	const isMobileViewActive = useSelector(
		(completeState: IStore): boolean => { 
			return completeState?.ui?.isMobileViewActive ?? false;
		});

	useEffect(
		() => {
			if (scrollContainer.current) {
				scrollContainer.current.scrollTo(0, 0);
			}
		},
		[allPagingInfo?.currentPage, orderBy.orderByRegion]
	);

	const confirmSelect = useCallback(
		() => {
			setRenderSelectByCountry(false);
			dispatch(actions.confirmSelectByCountry());
		},
		[dispatch]
	);

	const selectByCountryCode = useCallback(
		(countryCode: string) => {
			dispatch(actions.selectByCountryCode(countryCode));
		},
		[dispatch]
	);

	const changePage = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (e.target.value) {
				const number = parseInt(e.target.value);
				if (!Number.isNaN(number) && number >= 1 && number <= allPagingInfo.totalPages) {
					dispatch(actions.changeCurrentPage(number - 1));
				}
			}
		},
		[dispatch, allPagingInfo]
	);

	const changeItemsPerPage = useCallback(
		(value: string | null) => {
			if (value) {
				const number = parseInt(value);
				if (!Number.isNaN(number)) {
					dispatch(actions.changeItemsPerPage(number));
				}
			}
		},
		[dispatch]
	);

	const changePageRight = useCallback(
		() => {
			dispatch(actions.changeCurrentPage(allPagingInfo.currentPage + 1));
		},
		[dispatch, allPagingInfo]
	);

	const changePageLeft = useCallback(
		() => {
			dispatch(actions.changeCurrentPage(allPagingInfo.currentPage - 1));
		},
		[dispatch, allPagingInfo]
	);

	const filterByRegion = useCallback(
		(value: string | null) => {
			dispatch(actions.filterByRegion(currentCountry === value ? null :value));
		},
		[dispatch, currentCountry]
	);

	const filterBySubRegion = useCallback(
		(value: string | null) => {
			dispatch(actions.filterBySubRegion(currentCountry === value ? null : value));
		},
		[dispatch, currentCountry]
	);

	const orderByCriteria = useCallback(
		(value: string | null) => {
			if (value) {
				dispatch(actions.changeOrderCategory(value as OrderCategoriesEnum));
			}
		},
		[dispatch]
	);

	const changeViewType = useCallback(
		(value: string | null) => {
			 dispatch(actions.setReducedView(value === SCARVES_VIEW ? true : false));
		},
		[dispatch]
	);

	const teamGalleryScroll = useCallback(
		() => {
			
			dispatch(actions.setTrophyTooltip(null));
			dispatch(actions.setReserveTeamTooltip(null));
			dispatch(actions.hideDetailTrophyTooltip());
		},
		[dispatch]
	);

	const renderScarvesOfTeam = useCallback(
		(scarfInfo: IScarfInfo) => {
			return (
				<TeamInfoRenderer
					key={`${scarfInfo.team.name} info key`}
					scarfInfo={scarfInfo}
					section={section}
					isMapInfo={false}
				/>
			);
		},
		[section]
	);

	if (renderSelectByCountry) {
		const orderedCountryNameCodes: { name: string; countryCode: string; }[] = countryCodes
			.map(c => {
				const name = Localizer.getLocalizedCountryName(c);
				return {
					name,
					countryCode: c 
				};
			})
			.sort((a, b) => a.name > b.name ? 1 : -1);
		return (
			<SelectCountryFlexContainer>
				<StyledFilterByCountryButton
					onClick={confirmSelect}
				>
					<Link to={`/${SELECTED_COUNTRIES}`}>
						{'Select countries and click'}
					</Link>
				</StyledFilterByCountryButton>
				<CountryListContainer>
					{orderedCountryNameCodes.map(orderedCountryNameAndCode => {
						return (
							<StyledCountryRow
								isSelected={orderBy.countryCodes[orderedCountryNameAndCode.countryCode] ? true : false}
								onClick={() => selectByCountryCode(orderedCountryNameAndCode.countryCode)}
							>
								<span>
									<ImageLinkContainer src={`../../../img/flag/${orderedCountryNameAndCode.countryCode}.png`} alt={orderedCountryNameAndCode.name} />
								</span>
								<span>{orderedCountryNameAndCode.name}</span>
							</StyledCountryRow>
						);
					})}
				</CountryListContainer>
			</SelectCountryFlexContainer>
		);
	} else {
		return (
			<GalleryContainer ref={bckContainer}>
				<OrderContainer>
					<FilterContainerTopRow>
						<SortContainer>
							{
								allPagingInfo.totalPages > 1 &&
									<>
										{/* <TitleContainerWithoutPadding>{PAGE_KEY}</TitleContainerWithoutPadding> */}
										{allPagingInfo.currentPage > 0 ? <IconPagingContainer><i className='fa fa-arrow-left' onClick={changePageLeft}/></IconPagingContainer> : null}
										<PageNumberInput
											max={allPagingInfo.totalPages}
											min={1}
											value={allPagingInfo.currentPage + 1}
											onChange={changePage}
										/>
										<TitleContainerWithoutPadding>{`/${allPagingInfo.totalPages}`}</TitleContainerWithoutPadding>
										{allPagingInfo.currentPage + 1 < allPagingInfo.totalPages ? <IconPagingContainer><i className='fa fa-arrow-right' onClick={changePageRight}/></IconPagingContainer> : null}
										<DropdownContainerPaging>
											<Select
												selectedKey={allPagingInfo.teamsPerPage.toString()}
												items={ITEMS_PER_PAGE_ITEMS}
												onSelectionChange={changeItemsPerPage}
											/>
										</DropdownContainerPaging>
										
									</>
							}
							
							<FilterContainerIconsRow>
								{
									code !== ALL_COUNTRIES_NAME &&
									<MapIconContainer>
										<Link to={`/map?center=${name}`}>
											<i className='fa fa-map-marker' title={SHOW_MAP_KEY}/>
										</Link>
									</MapIconContainer>
								}
								<Select
									className={styles.iconSelect}
									placeholder={'Set view'}
									selectedKey={isReducedView ? SCARVES_VIEW : EXTENDED_VIEW}
									items={views}
									onSelectionChange={changeViewType}
									selectValueIcon={isReducedView ? 'fa fa-list' : 'fa fa-trophy'}
								/>	
								<Select
									className={styles.iconSelect}
									placeholder={'Order by'}
									selectedKey={orderBy.category}
									items={orderByCategoryItems}
									onSelectionChange={orderByCriteria}
									selectValueIcon={'fa fa-sort'}
								/>	
							</FilterContainerIconsRow>
						</SortContainer>
						
					</FilterContainerTopRow>
					<FilterContainerRow>
						<div className={isMobileViewActive ? styles.containerFlexMobile : styles.containerFlex}>
							{section === EnumSection.gallery && Countries.isCountrySelectableByRegion(currentCountry) &&
								<Select
									placeholder={`Filter by ${Countries.getCountryRegionName(currentCountry)}`}
									selectedKey={orderBy.orderByRegion || ''}
									items={regions}
									onSelectionChange={filterByRegion}
									popoverFixSize={true}
								/>
							}
							{section === EnumSection.gallery && Countries.isCountrySelectableBySubRegion(currentCountry) && subRegions.length > 1 &&
								<Select
									placeholder={`Filter by ${Countries.getCountrySubRegionName(currentCountry)}`}
									selectedKey={orderBy.orderBySubregion || ''}
									items={subRegions}
									onSelectionChange={filterBySubRegion}
									popoverFixSize={true}
								/>
							}
						</div>
						
					</FilterContainerRow>
				</OrderContainer>
				{
					trophyTooltip &&
						<TrophyTooltip
							trophyTooltip={trophyTooltip}
						/>
				}
				<ReserveTeamTooltip/>
				{
					detailTrophyTrophy &&
						<DetailTrophyTooltip
							detailTrophyTooltip={detailTrophyTrophy}
						/>
				}
				<ContentContainer ref={scrollContainer} onScroll={teamGalleryScroll}>
					{currentGroupScarves.map(x => renderScarvesOfTeam(x))}
				</ContentContainer>
			</GalleryContainer>
		);
	}
}
